import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import invariant from 'tiny-invariant';

/**
 * @name fetchSessionToken
 * 
 * This function is used to retrieve a session token from the backend. To
 * authenticate the player, the backend requires a Google access_token, which
 * is sent in the request body. Backend validates access_token through Google api. 
 * If player exists in the database, the backend responds with a session_token, 
 * which is used to authenticate the player on client side. If player does not exist in the database, 
 * the backend will make an attempt to register the player using data received from Google api
 * during the validation of access_token. If information received from Google is insufficient,
 * backend will respond with access_token and array of required fields. 
 * For example: ['address.street': 'field cannot be empty', "address.phonenumber": 'wrong format'].
 * 
 * Frontend is responsible for collecting the required data and sending it back to the backend along with access_token.
 * 
 * 
 * @param {Object} props - The props object containing necessary parameters.
 * @param {string} props.access_token - The access token required for authentication.
 * @param {string} props.apiUrl - The URL of the API endpoint.
 * @param {Function} props.onSuccess - The callback function to be executed on successful fetch.
 * @param {Function} props.onFailure - The callback function to be executed on failed fetch.
 * @param {Object} [props.additionalData] - Additional data to be included in the request body.
 * @returns {Promise} A promise that resolves with the fetched data or rejects with an error.
 * 
 * @example
 * 
 *  fetchGoogleToken({
      apiUrl: 'https://api.casinosaga.com',
      additionalData: { mobile: '1234567890', birthdate: '1990-01-01' },
      access_token: data.access_token,
      onSuccess: () => history.push('/'),
      onFailure: (err) => dispatch(Notifications.add({message: err.message, type: 'error'})),
    })
 */

function fetchSessionToken(props) {
  invariant(props.access_token, "access_token is required");
  invariant(props.apiUrl, "apiUrl is required"); // Create the request body.
  // If request is made in response to attempt to register a new user,
  // include additional data, requested by backend on the first call. For
  // example: { mobile: '1234567890', birthdate: '1990-01-01' }.

  var body = JSON.stringify(_objectSpread2({
    token: props.access_token
  }, props.additionalData));
  return fetch("".concat(props.apiUrl, "/google_auth"), {
    body,
    headers: {
      "Accept": "application/vnd.casinosaga.v1",
      "Content-Type": "application/json"
    },
    method: "POST"
  }).then(response => response.json()).then(data => {
    return props.onSuccess(data);
  }).catch(err => {
    // eslint-disable-next-line no-console
    console.error(err);
    return props.onFailure(err);
  });
}
fetchSessionToken.defaultProps = {
  additionalData: {}
};
fetchSessionToken.propsTypes = {
  access_token: PropTypes.string.isRequired,
  apiUrl: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  additionalData: PropTypes.object
};

export { fetchSessionToken };
